/**
 * RunDetailHeader — the status badges and facts line under the run title. Demo page: /ai (run status) on the
 * DetailPage template's `badges` / `meta` slots.
 */
import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge.js';
import { type DebugPayload, findFailedStep, humanizeStatus, statusBadgeVariant } from './run-detail-types.js';

const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

export const RunDetailBadges: React.FC<{ payload: DebugPayload }> = ({ payload }) => {
  const { run, graph } = payload;
  const failedStep = findFailedStep(graph.steps);
  const failedIndex = failedStep ? graph.steps.indexOf(failedStep) + 1 : null;
  return (
    <>
      <Badge variant={statusBadgeVariant(run.status)} dot size="md">
        {capitalize(humanizeStatus(run.status))}
      </Badge>
      <Badge variant="neutral" icon={<Clock size={12} aria-hidden="true" />} size="md">
        {capitalize(run.triggeredBy)}
      </Badge>
      {failedStep && failedIndex != null && (
        <Badge variant="danger" icon={<AlertTriangle size={12} aria-hidden="true" />} size="md">
          Step {failedIndex} of {graph.steps.length} failed
        </Badge>
      )}
    </>
  );
};

export const RunDetailMeta: React.FC<{ run: DebugPayload['run'] }> = ({ run }) => (
  <span className="text-sm text-muted">
    {run.workflowVersion != null ? `Workflow version ${run.workflowVersion}` : 'Workflow version unknown'}
  </span>
);
